import { FinanceiraOption } from '../../../../../models/SimulationBank/SimulationBankModels';
import { getFinanceiraLogo } from '../../../../../utils/FinanceiraLogoMap';

// Tile exibido no modal de autorização (ver FinanceirasConsentModal). `id` é o que o
// parceiro seleciona/desmarca; `canonicalIds` é o que efetivamente vai no payload de
// /simulation/screening/consult ao aceitar — para o tile "Soma" os dois divergem.
export interface FinanceiraTile {
    id: string;
    name: string;
    logo: string;
    authChannel?: string | null;
    canonicalIds: string[];
}

const isSomaCanonicalId = (canonicalId?: string | null): boolean =>
    !!canonicalId?.toUpperCase().startsWith('SOMA');

// Mesmo agrupamento usado no select de "Consulta de Propostas" por número (ver
// groupFinanceirasForSelect em SimulationBank.tsx): o parceiro não precisa saber qual
// bancarizadora da Soma vai processar a proposta, então elas viram um único tile "Soma".
// Diferente daquele select (que usa o id fixo "SOMA" só como filtro visual), aqui cada
// tile carrega em `canonicalIds` os canonical_id reais vindos da API — o tile "Soma"
// nunca hardcoda ["SOMA_UY3", "SOMA_CELCOIN"]: expande para o que a API realmente
// devolveu como ativo, então uma bancarizadora nova sob o guarda-chuva Soma entra
// automaticamente, sem deploy do front.
export const groupFinanceiraTiles = (options: FinanceiraOption[]): FinanceiraTile[] => {
    const soma = options.filter((f) => isSomaCanonicalId(f.canonical_id));
    const others = options.filter((f) => !isSomaCanonicalId(f.canonical_id));

    const tiles: FinanceiraTile[] = others.map((f) => ({
        id: f.canonical_id,
        name: f.name,
        logo: f.logo_url || getFinanceiraLogo(f.canonical_id),
        authChannel: f.auth_channel,
        canonicalIds: [f.canonical_id],
    }));

    if (soma.length > 0) {
        tiles.push({
            id: 'SOMA',
            name: 'Soma',
            logo: soma[0].logo_url || getFinanceiraLogo(soma[0].canonical_id),
            // Bancarizadoras do mesmo grupo compartilham o canal na prática — usa a primeira
            // que vier preenchida em vez de assumir que todas trazem o campo.
            authChannel: soma.find((f) => f.auth_channel)?.auth_channel,
            canonicalIds: soma.map((f) => f.canonical_id),
        });
    }

    return tiles;
};

// Expande os tiles selecionados nos canonical_ids reais a enviar no payload de submit —
// a string "SOMA" nunca deve chegar ao back, só existe como agrupamento visual.
export const expandSelectedCanonicalIds = (tiles: FinanceiraTile[], selectedIds: Set<string>): string[] =>
    tiles.filter((t) => selectedIds.has(t.id)).flatMap((t) => t.canonicalIds);

// Caminho inverso: a partir dos canonical_ids usados na última simulação (vindos de
// simulation_sessions.selected_financials), descobre quais tiles devem vir pré-marcados.
// Um tile do grupo "Soma" conta como selecionado se qualquer uma das financeiras que ele
// agrupa estiver na lista — financeira que saiu do ar (não virou tile, já filtrada como
// inativa antes de chegar aqui) simplesmente não casa com nenhum tile e fica de fora.
export const selectedTileIdsFromCanonicalIds = (tiles: FinanceiraTile[], canonicalIds: string[]): Set<string> => {
    const lastSelected = new Set(canonicalIds);
    return new Set(
        tiles.filter((t) => t.canonicalIds.some((id) => lastSelected.has(id))).map((t) => t.id)
    );
};
